import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { notify } from '../../lib/notifications.js';
import { cardGateDecision } from '../../lib/pendingOAuth.js';
import { clearResumeIntent, freePreviewState, readResumeIntent } from '../../lib/freePreview.js';
import { trackPreview, trackPreviewOnce } from '../../lib/freePreviewEvents.js';
import { freePublishingState } from '../../lib/freePublishing.js';
import { FreePreviewContext } from '../../contexts/FreePreviewContext.jsx';
import TrialWallOverlay from './TrialWallOverlay.jsx';

function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}

/** Mounted by the billing gate only for an authoritative Preview grant. */
export function FreePreviewProvider({ entitlements, onPaid, onLeave, children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(entitlements);
  const [wall, setWall] = useState(null);
  const wallRef = useRef(null);
  wallRef.current = wall;
  const state = useMemo(() => freePreviewState(ent) || { mode: 'hidden' }, [ent]);
  const active = ['active', 'readOnly', 'verify'].includes(state.mode);
  useEffect(() => { setEnt(entitlements); }, [entitlements]);
  useEffect(() => { if (active) trackPreviewOnce('preview_started', {}); }, [active]);

  // A cancelled checkout never reopens the wall.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') !== 'cancelled') return;
    const storage = sessionStore();
    const intent = readResumeIntent(storage);
    clearResumeIntent(storage);
    trackPreview('preview_checkout_cancelled', { source: intent?.source || null });
    notify({ level: 'info', title: t('freePreview.toastCancelled') });
    params.delete('checkout');
    params.delete('session_id');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    try {
      api.invalidateCache?.(['/api/billing/entitlements']);
      const next = await api.billing.getEntitlements({ fresh: true });
      const decision = cardGateDecision(next, { freePreview: true });
      setEnt(next);
      if (decision !== 'preview') {
        setWall(null);
        onLeave?.(decision, next);
      }
      return next;
    } catch { return null; }
  }, [onLeave]);

  const openTrialWall = useCallback((source = 'cta', meta = {}) => {
    // Free keeps main's existing plans/renewal flow rather than the legacy
    // new-signup card wall (which cannot authorize a returned-Free trial).
    if (freePublishingState(ent)) { navigate('/app/plans'); return; }
    if (!active || wallRef.current) return;
    const next = { source, meta: meta || {} };
    setWall(next);
    trackPreview('preview_wall_shown', { source, feature: meta?.feature || null, hasPost: Boolean(meta?.postId) });
  }, [active, ent, navigate]);
  const closeTrialWall = useCallback((how = 'back') => {
    const current = wallRef.current;
    if (!current) return;
    setWall(null);
    trackPreview('preview_wall_dismissed', { source: current.source, how });
  }, []);
  const value = useMemo(() => ({
    active, readOnly: state.mode === 'readOnly', pendingVerification: state.mode === 'verify', mode: state.mode,
    drafts: state.drafts || { ready: 0, limit: 3 }, imagesLimit: state.imagesLimit ?? 1, trialDays: state.trialDays || 7,
    entitlements: ent, wall, openTrialWall, closeTrialWall, refresh,
  }), [active, state, ent, wall, openTrialWall, closeTrialWall, refresh]);

  return (
    <FreePreviewContext.Provider value={value}>
      {children}
      {active && wall && <TrialWallOverlay wall={wall} entitlements={ent} onClose={closeTrialWall} onPaid={onPaid} />}
    </FreePreviewContext.Provider>
  );
}
export default FreePreviewProvider;
